/**
 * LoadingQueue - Sistema de carga progresiva por prioridad
 * Controla el orden de carga de las secciones del dashboard
 */
import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';

// Definición de fases y secciones
export const LOADING_PHASES = {
  PHASE_1: {
    id: 'phase1',
    name: 'Crítico',
    description: 'Mercados, Gráfico y Portafolio',
    sections: ['markets', 'chart', 'portfolio'],
    priority: 1,
  },
  PHASE_2: {
    id: 'phase2', 
    name: 'Secundario',
    description: 'Indicadores y Noticias',
    sections: ['indicators', 'news', 'tools'],
    priority: 2,
  },
  PHASE_3: {
    id: 'phase3',
    name: 'Terciario',
    description: 'Alertas y Extras',
    sections: ['alerts', 'correlations', 'dividends', 'backtesting'],
    priority: 3,
  },
};

// Configuración de secciones individuales
export const SECTION_CONFIG = {
  markets: {
    id: 'markets',
    name: 'Mercados Populares',
    phase: 'phase1',
    priority: 1,
    icon: '📊',
  },
  chart: {
    id: 'chart',
    name: 'Gráfico Principal',
    phase: 'phase1',
    priority: 2,
    icon: '📈',
  },
  portfolio: {
    id: 'portfolio',
    name: 'Mi Portafolio',
    phase: 'phase1',
    priority: 3,
    icon: '💼',
  },
  indicators: {
    id: 'indicators',
    name: 'Indicadores Técnicos',
    phase: 'phase2',
    priority: 4,
    icon: '🔢',
  },
  news: {
    id: 'news',
    name: 'Noticias',
    phase: 'phase2',
    priority: 5,
    icon: '📰',
  },
  tools: {
    id: 'tools',
    name: 'Herramientas',
    phase: 'phase2',
    priority: 6,
    icon: '🛠️',
  },
  alerts: {
    id: 'alerts',
    name: 'Alertas',
    phase: 'phase3',
    priority: 7,
    icon: '🔔',
  },
  correlations: {
    id: 'correlations',
    name: 'Análisis Correlación',
    phase: 'phase3',
    priority: 8,
    icon: '🔗',
  },
  dividends: {
    id: 'dividends',
    name: 'Dividendos',
    phase: 'phase3',
    priority: 9,
    icon: '📅',
  },
  backtesting: {
    id: 'backtesting',
    name: 'Backtesting',
    phase: 'phase3',
    priority: 10,
    icon: '⏪',
  },
};

const LoadingQueueContext = createContext(null);

export const useLoadingQueue = () => {
  const context = useContext(LoadingQueueContext);
  if (!context) {
    throw new Error('useLoadingQueue must be used within a LoadingQueueProvider');
  }
  return context;
};

export const LoadingQueueProvider = ({ children }) => {
  // Estado de las fases
  const [currentPhase, setCurrentPhase] = useState(null);
  const [completedPhases, setCompletedPhases] = useState([]);
  
  // Estado de las secciones
  const [loadingSections, setLoadingSections] = useState([]);
  const [completedSections, setCompletedSections] = useState([]);
  const [enabledSections, setEnabledSections] = useState([]);
  
  // Estado general
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  
  // Modo Carga Rápida - solo carga Fase 1 (Mercados, Gráfico, Portafolio)
  const [fastLoadMode, setFastLoadMode] = useState(() => {
    const saved = localStorage.getItem('fastLoadMode');
    return saved === 'true';
  });
  
  // Refs para evitar race conditions
  const loadingRef = useRef(false);
  
  // Guardar preferencia de modo rápido en localStorage
  useEffect(() => {
    localStorage.setItem('fastLoadMode', fastLoadMode.toString());
  }, [fastLoadMode]);
  
  // Toggle modo carga rápida
  const toggleFastLoadMode = useCallback(() => {
    setFastLoadMode(prev => !prev);
  }, []);
  
  // Calcular progreso basado en secciones completadas y modo
  const calculateProgress = useCallback(() => {
    if (fastLoadMode) {
      // En modo rápido, solo contar secciones de Fase 1
      const phase1Sections = LOADING_PHASES.PHASE_1.sections;
      const completed = completedSections.filter(s => phase1Sections.includes(s)).length;
      return Math.round((completed / phase1Sections.length) * 100);
    }
    const totalSections = Object.keys(SECTION_CONFIG).length;
    const completed = completedSections.length;
    return Math.round((completed / totalSections) * 100);
  }, [completedSections, fastLoadMode]);
  
  // Actualizar progreso cuando cambian las secciones completadas
  useEffect(() => {
    setProgress(calculateProgress());
  }, [completedSections, calculateProgress]);
  
  // Iniciar la cola de carga
  const startLoadingQueue = useCallback(() => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    
    setIsLoading(true);
    setCurrentPhase('phase1');
    setCompletedPhases([]);
    setLoadingSections([]);
    setCompletedSections([]);
    
    // Habilitar secciones de Fase 1 inmediatamente
    const phase1Sections = LOADING_PHASES.PHASE_1.sections;
    setEnabledSections(phase1Sections);
    
    // NOTA: chart y portfolio ahora reportan su carga desde sus componentes reales
    // usando useComponentLoading de LoadingContext - NO auto-completar con setTimeout
  }, []);
  
  // Marcar una sección como cargando
  const startSectionLoading = useCallback((sectionId) => {
    setLoadingSections(prev => [...new Set([...prev, sectionId])]);
  }, []);
  
  // Marcar una sección como completada
  const completeSectionLoading = useCallback((sectionId) => {
    setLoadingSections(prev => prev.filter(s => s !== sectionId));
    setCompletedSections(prev => [...new Set([...prev, sectionId])]);
  }, []);
  
  // Verificar si una fase está completa y avanzar a la siguiente
  useEffect(() => {
    if (!currentPhase || !isLoading) return;
    
    const phaseConfig = Object.values(LOADING_PHASES).find(p => p.id === currentPhase);
    if (!phaseConfig) return;
    
    // Verificar si todas las secciones de la fase actual están completas
    const allSectionsComplete = phaseConfig.sections.every(
      section => completedSections.includes(section)
    );
    
    if (allSectionsComplete) {
      // Marcar fase como completada
      setCompletedPhases(prev => [...new Set([...prev, currentPhase])]);
      
      // En modo rápido, terminar después de Fase 1
      if (fastLoadMode && currentPhase === 'phase1') {
        setCurrentPhase(null);
        setIsLoading(false);
        loadingRef.current = false;
        return;
      }
      
      // Avanzar a la siguiente fase (modo normal)
      if (currentPhase === 'phase1') {
        setCurrentPhase('phase2');
        const phase2Sections = LOADING_PHASES.PHASE_2.sections;
        setEnabledSections(prev => [...prev, ...phase2Sections]);
        
        // NOTA: Fase 2 se completa cuando sus componentes reportan carga real
        // usando LoadingContext - NO auto-completar con setTimeout
      } else if (currentPhase === 'phase2') {
        setCurrentPhase('phase3');
        const phase3Sections = LOADING_PHASES.PHASE_3.sections;
        setEnabledSections(prev => [...prev, ...phase3Sections]);
        
        // NOTA: Fase 3 se completa cuando sus componentes reportan carga real
        // usando LoadingContext - NO auto-completar con setTimeout
      } else if (currentPhase === 'phase3') {
        // Todas las fases completas
        setCurrentPhase(null);
        setIsLoading(false);
        loadingRef.current = false;
      }
    }
  }, [currentPhase, completedSections, isLoading, fastLoadMode]);
  
  // Verificar si una sección está habilitada para cargar
  const isSectionEnabled = useCallback((sectionId) => {
    return enabledSections.includes(sectionId);
  }, [enabledSections]);
  
  // Verificar si una sección está cargando
  const isSectionLoading = useCallback((sectionId) => {
    return loadingSections.includes(sectionId);
  }, [loadingSections]);
  
  // Verificar si una sección está completada
  const isSectionComplete = useCallback((sectionId) => {
    return completedSections.includes(sectionId);
  }, [completedSections]);
  
  // Obtener el estado de una sección
  const getSectionStatus = useCallback((sectionId) => {
    if (completedSections.includes(sectionId)) return 'complete';
    if (loadingSections.includes(sectionId)) return 'loading';
    if (enabledSections.includes(sectionId)) return 'pending';
    return 'waiting';
  }, [completedSections, loadingSections, enabledSections]);
  
  // Resetear la cola
  const resetQueue = useCallback(() => {
    setCurrentPhase(null);
    setCompletedPhases([]);
    setLoadingSections([]);
    setCompletedSections([]);
    setEnabledSections([]);
    setIsLoading(false);
    setProgress(0);
    loadingRef.current = false;
  }, []);
  
  // Forzar completar todas las secciones (para datos en caché)
  const forceCompleteAll = useCallback(() => {
    const allSections = fastLoadMode 
      ? LOADING_PHASES.PHASE_1.sections 
      : Object.keys(SECTION_CONFIG);
    setCompletedSections(allSections);
    setEnabledSections(allSections);
    setCompletedPhases(fastLoadMode ? ['phase1'] : ['phase1', 'phase2', 'phase3']);
    setCurrentPhase(null);
    setIsLoading(false);
    setProgress(100);
    loadingRef.current = false;
  }, [fastLoadMode]);
  
  const value = {
    // Estado
    currentPhase,
    completedPhases,
    loadingSections,
    completedSections,
    enabledSections,
    isLoading,
    progress,
    
    // Modo Carga Rápida
    fastLoadMode,
    toggleFastLoadMode,
    
    // Acciones
    startLoadingQueue,
    startSectionLoading,
    completeSectionLoading,
    resetQueue,
    forceCompleteAll,
    
    // Helpers
    isSectionEnabled,
    isSectionLoading,
    isSectionComplete,
    getSectionStatus,
    
    // Configuración
    LOADING_PHASES,
    SECTION_CONFIG,
  };
  
  return (
    <LoadingQueueContext.Provider value={value}>
      {children}
    </LoadingQueueContext.Provider>
  );
};

export default LoadingQueueContext;
